"use client";

import React, { useState, useEffect, useRef } from 'react'
import ProfileImage from './profile-image'
import { cn } from '@/shared/utils/tw'
import { ChatMessageType } from '@/features/text-chat-with-agent/types/chat-message'
import { useTranslation } from 'react-i18next'
import { liquidGlassStyles } from '@/shared/ui/avatar-design-system/styles/liquid-glass'
import { Database, ChevronRight } from 'lucide-react'
import { MessageWithLinks } from '@/shared/ui/message-with-links'

/**
 * ChatLoading - ai-companion-next/loading-indicator.tsx:34-47 그대로
 * animate-bounce + animationDelay 패턴
 */
const ChatLoading = ({ profileImageUrl }: { profileImageUrl?: string }) => {
  return (
    <div
      className={cn(
        "flex justify-start items-start gap-2",
        "animate-slide-up"
      )}
    >
      {/* Avatar Thumbnail */}
      <ProfileImage imageUrl={profileImageUrl || "/webp/kikiz-profile.webp"} />

      {/* Loading Dots - ai-companion-next 패턴 */}
      <div className="flex items-center gap-1 py-2">
        <span
          className="w-2 h-2 bg-black/40 rounded-full animate-bounce"
          style={{ animationDelay: "0ms" }}
        />
        <span
          className="w-2 h-2 bg-black/40 rounded-full animate-bounce"
          style={{ animationDelay: "150ms" }}
        />
        <span
          className="w-2 h-2 bg-black/40 rounded-full animate-bounce"
          style={{ animationDelay: "300ms" }}
        />
      </div>
    </div>
  )
}

/**
 * ContextIndicator - RAG 컨텍스트 표시 (Liquid Glass 스타일)
 * BM25/Vector 출처 표시
 */
interface ContextIndicatorProps {
  contextUsed?: ChatMessageType['context_used'];
}

const ContextIndicator: React.FC<ContextIndicatorProps> = ({ contextUsed }) => {
  const [expanded, setExpanded] = useState(false);

  if (!contextUsed?.documents || contextUsed.documents.length === 0) return null;

  const docs = contextUsed.documents;
  const qaPairs = docs.filter(d => d.metadata?.source_type === 'qa_pair' || (d.metadata?.question && d.metadata?.answer));
  const bm25Count = docs.filter(d => d.metadata?.source === 'bm25').length;
  const vectorCount = docs.filter(d => d.metadata?.source === 'vector').length;

  return (
    <div className="mt-2">
      {/* 토글 버튼 */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-[10px] text-white/60 hover:text-white/80 transition-colors"
      >
        <ChevronRight
          className={cn("h-3 w-3 transition-transform", expanded && "rotate-90")}
        />
        <Database className="h-3 w-3" />
        <span>참조 {contextUsed.count}건</span>
        {vectorCount > 0 && (
          <span className="px-1.5 py-0.5 rounded text-[9px] bg-blue-500/30 text-blue-200">
            Vector {vectorCount}
          </span>
        )}
        {bm25Count > 0 && (
          <span className="px-1.5 py-0.5 rounded text-[9px] bg-orange-500/30 text-orange-200">
            BM25 {bm25Count}
          </span>
        )}
      </button>

      {/* 확장된 컨텍스트 */}
      {expanded && (
        <div className="mt-2 pl-2 border-l border-white/20 space-y-1.5">
          {qaPairs.slice(0, 5).map((qa, idx) => (
            <div key={idx} className="text-[11px] text-white/70">
              <div className="flex items-center gap-1.5">
                <span className={cn(
                  "px-1.5 py-0.5 rounded text-[9px] font-medium",
                  qa.metadata?.source === 'bm25'
                    ? "bg-orange-500/30 text-orange-200"
                    : qa.metadata?.source === 'vector'
                    ? "bg-blue-500/30 text-blue-200"
                    : "bg-white/20 text-white/60"
                )}>
                  {((qa.score || 0) * 100).toFixed(0)}%
                </span>
                <span className="truncate">{qa.metadata?.question || qa.content?.slice(0, 50)}</span>
              </div>
            </div>
          ))}
          {qaPairs.length > 5 && (
            <div className="text-[10px] text-white/50">
              +{qaPairs.length - 5}건 더...
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * AIMessage - ai-companion-next/ai-message.tsx 그대로
 * 타이핑 효과 + Liquid Glass 스타일
 */
interface AIMessageProps {
  content: string;
  isStreaming?: boolean;
  className?: string;
  profileImageUrl?: string;
  contextUsed?: ChatMessageType['context_used'];
}

const AIMessage: React.FC<AIMessageProps> = ({
  content,
  isStreaming = false,
  className,
  profileImageUrl = '/webp/kikiz-profile.webp',
  contextUsed,
}) => {
  // 타이핑 효과를 위한 표시 텍스트 상태 (ai-companion-next/ai-message.tsx:20)
  const initialLengthRef = useRef(content.length);
  const [displayedText, setDisplayedText] = useState(content);

  // 타이핑 효과 (ai-companion-next/ai-message.tsx:23-40)
  useEffect(() => {
    if (content.length <= initialLengthRef.current) {
      setDisplayedText(content);
      return;
    }

    // 새로운 글자가 추가되었을 때만 타이핑 효과
    if (content.length > displayedText.length) {
      const timer = setTimeout(() => {
        setDisplayedText(content.slice(0, displayedText.length + 1));
      }, 30); // 30ms마다 1글자 추가

      return () => clearTimeout(timer);
    }
  }, [content, displayedText]);

  return (
    <div
      className={cn(
        "flex justify-start items-start gap-2",
        "animate-slide-up",
        className
      )}
    >
      {/* Avatar Thumbnail */}
      <ProfileImage imageUrl={profileImageUrl} />

      {/* Message Bubble + Context */}
      <div className="flex flex-col max-w-[calc(80%-50px)]">
        {/* Message Bubble - ai-companion-next/ai-message.tsx:62-80 */}
        <div
          className={cn(
            "rounded-[16px]",
            "px-[18px] py-[10px]",
            "font-pretendard",
            "text-[15px] font-medium",
            "text-black",
            "leading-[1.4]",
            "whitespace-pre-wrap break-words"
          )}
          style={liquidGlassStyles.aiBubble}
        >
          <MessageWithLinks text={displayedText} />
          {/* 커서 */}
          {(isStreaming || displayedText.length < content.length) && (
            <span className="inline-block w-1 h-4 ml-0.5 bg-black/40 animate-pulse" />
          )}
        </div>

        {/* RAG 컨텍스트 표시 (스트리밍 완료 후에만) */}
        {!isStreaming && contextUsed && (
          <ContextIndicator contextUsed={contextUsed} />
        )}
      </div>
    </div>
  );
};

/**
 * UserMessage - ai-companion-next/user-message.tsx 그대로
 * slide-up 애니메이션 + Liquid Glass 스타일
 */
interface UserMessageProps {
  content: string;
  className?: string;
}

const UserMessage: React.FC<UserMessageProps> = ({
  content,
  className,
}) => {
  return (
    <div
      className={cn(
        "flex justify-end",
        "animate-slide-up",
        className
      )}
    >
      {/* Message Bubble */}
      <div
        className={cn(
          "max-w-[80%]",
          "rounded-[16px]",
          "px-[18px] py-[10px]",
          "font-pretendard",
          "text-[15px] font-medium",
          "text-black",
          "leading-[1.4]",
          "whitespace-pre-wrap break-words"
        )}
        style={liquidGlassStyles.userBubble}
      >
        {content}
      </div>
    </div>
  );
};

/**
 * ChatMessage - 기존 인터페이스 유지하면서 내부 구현 변경
 */
export const ChatMessage = ({
  sender,
  message,
  showProfileImage = false,
  profileImageUrl = '/webp/kikiz-profile.webp',
  isStreaming = false,
  contextUsed
}: {
  sender: 'user' | 'agent',
  message: string,
  showProfileImage?: boolean,
  profileImageUrl?: string,
  isStreaming?: boolean,
  contextUsed?: ChatMessageType['context_used']
}) => {
  if (sender === 'agent') {
    return (
      <AIMessage
        content={message}
        isStreaming={isStreaming}
        profileImageUrl={profileImageUrl}
        contextUsed={contextUsed}
      />
    );
  }

  return <UserMessage content={message} />;
}

/**
 * ChatMessageList - 기존 인터페이스 유지
 */
export const ChatMessageList = ({
  messages,
  isLoading = false,
  profileImageUrl
}: {
  messages: ChatMessageType[],
  isLoading?: boolean,
  profileImageUrl?: string
}) => {
  // 스트리밍 중인 메시지가 있는지 확인
  const hasStreamingMessage = messages.some(msg => msg.isStreaming);
  const { t } = useTranslation();

  return (
    <div className="flex flex-col pb-[80px]" style={{ gap: 'calc(var(--spacing) * 3)' }}>
      {messages.map((message, index) => (
        <ChatMessage
          key={index}
          sender={message.sender}
          message={message.messageKey ? t(message.messageKey) : message.message || ''}
          showProfileImage={message.sender === 'agent'}
          profileImageUrl={profileImageUrl}
          isStreaming={message.isStreaming}
          contextUsed={message.context_used}
        />
      ))}
      {/* 로딩 상태 표시 (스트리밍 메시지가 없고 로딩 중일 때만) */}
      {isLoading && !hasStreamingMessage && <ChatLoading profileImageUrl={profileImageUrl} />}
    </div>
  )
}
